import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import InventoryList from './InventoryList';
import { useQuery } from '@apollo/client';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { useDispatch, useI18n } from '~Hooks';
import gql from 'graphql-tag';
import moment, { Moment } from 'moment';
import { useEntryFinder } from '~Components/EntryFinder';
import navigateLeftIcon from '~/Assets/icons//navigate-left.svg';
import navigateRightIcon from '~/Assets/icons//navigate-right.svg';
import { addToast } from '~/actions';

const Comment = styled.textarea`
  margin-bottom: 8px;
  box-sizing: border-box;
  width: 100%;
  height: 100px;
  border-radius: 4px;
  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  padding: 16px;
  font-size: 16px;
  color: #1c232c;
  outline: #1c232c;
  border-color: #e8e9ea;
  box-shadow: 0px 1px 3px 0px rgb(0 0 0 / 50%);
  transition: border-color 0.2s ease-in-out;

  &:focus {
    border-color: #1c232c;
  }
`;

const DateNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const Prev = styled.div`
  width: 48px;
  height: 48px;
  mask-image: url('${navigateLeftIcon}');
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: #3a3a3a;
  margin-right: 12px;
`;

const Next = styled.div`
  width: 48px;
  height: 48px;
  mask-image: url('${navigateRightIcon}');
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: #3a3a3a;
  margin-left: 12px;
`;

const DateText = styled.span`
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 20px;
`;

export type Item = {
  itemId: string;
  count: number;
  name?: string;
};

interface Props {
  venueItems: { id: string; title: string }[];
  loading: boolean;
  goBack: () => void;
}

const CreateOrder: React.FC<Props> = ({ venueItems, loading, goBack }) => {
  const [, , venueId] = location.hash.split('/');
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const [inventoryItems, setInventoryItems] = useState<Item[]>([]);
  const [itemsToOrder, setItemsToOrder] = useState<Item[]>([]);
  const [chosenDate, setChosenDate] = useState<Moment>(moment().add(1, 'day'));
  const [comment, setComment] = useState('');
  const universe = useInventoryUniverse();
  const findEntryByItemId = useEntryFinder();
  const api = useInventoryApi(venueId);

  useEffect(() => {
    if (!venueItems) return;

    const items: Item[] = [];

    universe.items.map((item) => {
      const matchingItem = venueItems.find((i: { id: string }) => i.id === item.itemId);
      const entry = findEntryByItemId(item.itemId);

      if (matchingItem && matchingItem?.title && entry) {
        const newItem = {
          name: matchingItem.title,
          count: 0,
          itemId: matchingItem.id,
        };
        items.push(newItem);
      }
    });
    setInventoryItems(items);
  }, [universe, venueItems]);

  const previousDate = () => {
    if (moment(chosenDate).isSame(moment(), 'day')) return;

    const prev = moment(chosenDate).subtract(1, 'day');
    setChosenDate(prev);
  };

  const nextDate = () => {
    const next = moment(chosenDate).add(1, 'day');
    setChosenDate(next);
  };

  const submitOrder = () => {
    const filtered = itemsToOrder.filter((item) => item.count !== 0);
    const itemArray = filtered.map(({ name, ...keepAttrs }) => keepAttrs);

    api.addRequest(chosenDate.toDate(), itemArray, comment);
    dispatch(addToast(i18n('TOAST.Inventory.Order'), true));
    goBack();
  };

  return (
    <>
      <DateNav>
        <Prev onTouchStart={previousDate} />
        <DateText>{chosenDate.format('YYYY-MM-DD')}</DateText>
        <Next onTouchStart={nextDate} />
      </DateNav>
      <Comment
        placeholder={i18n('Inventory.OrderCommentPlaceholder')}
        onChange={(e) => setComment(e.target.value)}
      />
      <InventoryList
        loading={loading}
        inventoryItems={inventoryItems}
        setInventoryItems={(val: Item[]) => setItemsToOrder(val)}
        handleSubmit={submitOrder}
      />
    </>
  );
};

export default CreateOrder;
